import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router';
import { AppShell, DashboardHome } from './App';
import { LoginPage } from './pages/LoginPage';
import { FlowsListPage } from './pages/FlowsListPage';
import { FlowsNewPage } from './pages/FlowsNewPage';
import { FlowsNewEntryPage } from './pages/FlowsNewEntryPage';
import { FlowDetailPage } from './pages/FlowDetailPage';
import { PatientsPage } from './pages/PatientsPage';
import { PatientDetailPage } from './pages/PatientDetailPage';
import { EncountersListPage } from './pages/EncountersListPage';
import { EncounterDetailPage } from './pages/EncounterDetailPage';
import { UsageDashboardPage } from './pages/UsageDashboardPage';
import { SettingsLayout } from './pages/settings/SettingsLayout';
import { IdentityPage } from './pages/settings/IdentityPage';
import { BrandPage } from './pages/settings/BrandPage';
import { LlmPage } from './pages/settings/LlmPage';
import { VoicePage } from './pages/settings/VoicePage';
import {
  EmailPage,
  LimitsPage,
  PrivacyPage,
  RetentionPage,
} from './pages/settings/LimitsRetentionEmailPrivacyPages';
import { UsersPage } from './pages/settings/UsersPage';
import { AuditLogPage } from './pages/settings/AuditLogPage';
import { SecurityPage } from './pages/settings/SecurityPage';
import { Setup2faPage } from './pages/settings/Setup2faPage';
import { RequireAuth } from './auth/RequireAuth';
import { IntegrationsLayout } from './pages/integrations/IntegrationsLayout';
import { ApiKeysPage } from './pages/integrations/ApiKeysPage';
import { WebhooksPage } from './pages/integrations/WebhooksPage';
import { KioskPage } from './pages/integrations/KioskPage';
import { RetentionPage as IntegrationsRetentionPage } from './pages/integrations/RetentionPage';
import { ApiDocsPage } from './pages/integrations/ApiDocsPage';
import './index.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 60_000,
    },
  },
});

// Sessione scaduta (refresh fallito o non possibile): hard redirect a login.
// L'evento è dispatchato da packages/admin/src/lib/api/_request.ts quando un 401
// non recuperabile arriva su una rotta non-auth. Non ridirezioniamo se siamo già
// sulla pagina di login per evitare loop.
if (typeof window !== 'undefined') {
  window.addEventListener('audiq:auth-expired', () => {
    if (window.location.pathname !== '/admin/login') {
      window.location.assign('/admin/login');
    }
  });
}

const root = document.getElementById('root');
if (!root) throw new Error('#root non trovato');

ReactDOM.createRoot(root).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter basename="/admin">
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route
            element={
              <RequireAuth>
                <AppShell />
              </RequireAuth>
            }
          >
            <Route index element={<DashboardHome />} />
            <Route path="/flows" element={<FlowsListPage />} />
            <Route path="/flows/new" element={<FlowsNewEntryPage />} />
            <Route path="/flows/new/scratch" element={<FlowsNewPage />} />
            <Route path="/flows/:id" element={<FlowDetailPage />} />
            <Route path="/patients" element={<PatientsPage />} />
            <Route path="/patients/:pseudonym" element={<PatientDetailPage />} />
            <Route path="/encounters" element={<EncountersListPage />} />
            <Route path="/encounters/:id" element={<EncounterDetailPage />} />
            <Route path="/usage" element={<UsageDashboardPage />} />
            <Route path="/settings" element={<SettingsLayout />}>
              <Route index element={<Navigate to="/settings/identity" replace />} />
              <Route path="identity" element={<IdentityPage />} />
              <Route path="brand" element={<BrandPage />} />
              <Route path="llm" element={<LlmPage />} />
              <Route path="voice" element={<VoicePage />} />
              <Route path="limits" element={<LimitsPage />} />
              <Route path="retention" element={<RetentionPage />} />
              <Route path="email" element={<EmailPage />} />
              <Route path="privacy" element={<PrivacyPage />} />
              <Route path="users" element={<UsersPage />} />
              <Route path="audit" element={<AuditLogPage />} />
              <Route path="security" element={<SecurityPage />} />
              <Route path="2fa-setup" element={<Setup2faPage />} />
            </Route>
            <Route path="/integrations" element={<IntegrationsLayout />}>
              <Route index element={<Navigate to="/integrations/api-keys" replace />} />
              <Route path="api-keys" element={<ApiKeysPage />} />
              <Route path="webhooks" element={<WebhooksPage />} />
              <Route path="kiosk" element={<KioskPage />} />
              <Route path="retention" element={<IntegrationsRetentionPage />} />
              <Route path="api-docs" element={<ApiDocsPage />} />
            </Route>
            <Route path="/security/2fa-setup-required" element={<Setup2faPage forceMode />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>,
);
